// src/@base/arrays.ts
function concat(array, ...values) {
  if (Array.isArray(array)) return [...array].concat(...values);
  return [array].concat(...values);
}

// src/@base/chunk.ts
function overflowChunk(arr, chunkSize, objSize) {
  if (!arr?.length) return [];
  const result = [[]];
  let currentChunk = result[0];
  let currentSize = 0;
  const getSizeFunc = typeof objSize === "function" ? (obj) => objSize(obj) : () => objSize;
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    const itemSize = getSizeFunc(item);
    if (currentSize + itemSize > chunkSize) {
      currentChunk = [item];
      result.push(currentChunk);
      currentSize = itemSize;
    } else {
      currentChunk.push(item);
      currentSize += itemSize;
    }
  }
  return result;
}
function chunk(arr, chunkSize) {
  if (!arr?.length) return [];
  if (chunkSize <= 0) return [arr];
  const resultSize = Math.ceil(arr.length / chunkSize);
  const result = new Array(resultSize);
  for (let i = 0; i < resultSize; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, arr.length);
    const chunkLength = end - start;
    const chunk2 = new Array(chunkLength);
    for (let j = 0; j < chunkLength; j++) {
      chunk2[j] = arr[start + j];
    }
    result[i] = chunk2;
  }
  return result;
}

// src/@base/parsers/data-transform.ts
import dayjs from "dayjs";
function parseDataTransform(data, options) {
  if (!data || Object.keys(data).length === 0) {
    return {};
  }
  const { dateFormat = "toDate" } = options || {};
  let matricula = "";
  return Object.entries(data).map(([key, value]) => {
    if (key === "matricula") {
      matricula = String(value);
    }
    if (["cpf", "cpfResponsavel"].includes(key)) {
      return [key, String(value).replace(/\D/g, "")];
    }
    if (key === "cnpj") {
      return [
        key,
        String(value).toUpperCase().replace(/[^A-Z0-9]/g, "")
      ];
    }
    if (key === "email") {
      return [key, value];
    }
    if (key === "repCodigo" || key === "repCodBarras" || key === "repProximidade" || key === "repSenha" || key === "cep" || key === "numero") {
      if (value == null) return [key, ""];
    }
    if (typeof value === "object") {
      if (value === null) {
        return [key, void 0];
      }
      if (Object.keys(value).length === 0) {
        return [key, void 0];
      }
      return [
        key,
        parseDataTransform(value, { dateFormat })
      ];
    }
    if (typeof value === "boolean") {
      return [key, value ? "S" : "N"];
    }
    if (typeof value !== "undefined") {
      const valueIsNum = Number(value);
      const [year, month, day, ...rest] = String(value).split("-");
      const replace = String(value).replace(/[#$%&._*-]/g, "");
      const setNumber = Number(replace);
      if (year && month && day && rest.length === 0) {
        const toDate = dayjs(value).toDate();
        const toStr = dayjs(value).format("YYYY-MM-DD");
        const date = dateFormat === "toDate" ? toDate : toStr;
        return [key, date];
      }
      if (replace.length >= 11) {
        return [key, replace];
      }
      if (valueIsNum !== 0 && Number.isNaN(valueIsNum) && (!year || !month || !day)) {
        if (setNumber !== 0 && !Number.isNaN(setNumber) && setNumber <= 2147483647 && setNumber >= -2147483648) {
          return [key, setNumber];
        }
        return [key, replace];
      }
      if (valueIsNum === 0 && key !== "reconhecimentoFacial") {
        return [key, void 0];
      }
      if (valueIsNum !== 0 && !Number.isNaN(valueIsNum) && valueIsNum <= 2147483647 && valueIsNum >= -2147483648) {
        return [key, valueIsNum];
      }
      return [key, String(valueIsNum)];
    }
    return [key, void 0];
  }).filter(([, value]) => value !== void 0).reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
}

// src/@base/parsers/datetime.ts
import dayjs2 from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs2.extend(utc);
var dateTimePatternDate = ({ utc: utc4 }) => {
  return dayjs2.utc(utc4).format("YYYY-MM-DDT[03:00:00.000Z]");
};
var DateTimePatternString = (utc4) => {
  if (typeof utc4 === "string") {
    const [ano2, mes2] = utc4.split("-");
    return `${ano2}-${mes2}-01T03:00:00.000Z`;
  }
  const mes = utc4.getMonth() + 1;
  const ano = utc4.getFullYear();
  return `${ano}-0${mes}-01T03:00:00.000Z`;
};
var hoursToMinutes = (hour = "00:00", ...hours) => {
  if (hour !== "" && hour !== null) {
    try {
      let tot = 0;
      const [h, m] = hour.split(":");
      if (hours?.length) for (const h2 of hours) tot += hoursToMinutes(h2);
      return Number(h) * 60 + Number(m) + tot;
    } catch (e) {
      console.error(hour, e);
      return 0;
    }
  }
  return 0;
};
var minutesToHours = (minute = 0, display_seconds = false) => {
  if (minute < 0) {
    console.trace(`Minutos n\xE3o podem ser negativos (${minute})`);
    return "00:00";
  }
  const hh = Math.floor(minute / 60);
  const mm = minute % 60;
  const ss = Math.floor(minute % 1 * 60);
  const h = hh.toString().padStart(2, "0");
  const m = display_seconds ? Math.floor(mm).toString().padStart(2, "0") : Math.round(mm).toString().padStart(2, "0");
  const s = ss.toString().padStart(2, "0");
  if (display_seconds) return `${h}:${m}:${s}`;
  return `${h}:${m}`;
};
var formatarHoras = (valor = 0, digitos = 2) => {
  if (typeof valor === "number") valor = minutesToHours(valor);
  if (!valor) return `${"0".repeat(digitos)}:00`;
  const [hours, minutes] = valor.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes))
    throw new Error("Horas e minutos devem ser n\xFAmeros");
  return `${hours.toString().padStart(digitos, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// src/@base/parsers/origem-registros.ts
var Origens = {
  1: "Manual Supervisor",
  2: "Manual Funcion\xE1rio",
  3: "Manual Tela de Digita\xE7\xE3o GestorP",
  4: "Biometria REP Terminal",
  5: "Teclado REP Terminal",
  6: "REP",
  7: "Aplicativo Ponto",
  8: "Aplicativo Ponto Manual",
  9: "Aplicativo Ponto Web",
  10: "Aplicativo Ponto Coletivo",
  11: "Ponto Exce\xE7\xE3o",
  12: "Ponto Pr\xE9 Assinalado",
  13: "Controle de Acesso"
};
var OrigensEnum = /* @__PURE__ */ ((OrigensEnum2) => {
  OrigensEnum2[OrigensEnum2["MANUAL_SUPERVISOR"] = 1] = "MANUAL_SUPERVISOR";
  OrigensEnum2[OrigensEnum2["MANUAL_FUNCIONARIO"] = 2] = "MANUAL_FUNCIONARIO";
  OrigensEnum2[OrigensEnum2["MANUAL_TELA_DIGITACAO_GESTORP"] = 3] = "MANUAL_TELA_DIGITACAO_GESTORP";
  OrigensEnum2[OrigensEnum2["BIOMETRIA_REP_TERMINAL"] = 4] = "BIOMETRIA_REP_TERMINAL";
  OrigensEnum2[OrigensEnum2["TECLADO_REP_TERMINAL"] = 5] = "TECLADO_REP_TERMINAL";
  OrigensEnum2[OrigensEnum2["REP"] = 6] = "REP";
  OrigensEnum2[OrigensEnum2["APLICATIVO_PONTO"] = 7] = "APLICATIVO_PONTO";
  OrigensEnum2[OrigensEnum2["APLICATIVO_PONTO_MANUAL"] = 8] = "APLICATIVO_PONTO_MANUAL";
  OrigensEnum2[OrigensEnum2["APLICATIVO_PONTO_WEB"] = 9] = "APLICATIVO_PONTO_WEB";
  OrigensEnum2[OrigensEnum2["APLICATIVO_PONTO_COLETIVO"] = 10] = "APLICATIVO_PONTO_COLETIVO";
  OrigensEnum2[OrigensEnum2["PONTO_EXCECAO"] = 11] = "PONTO_EXCECAO";
  OrigensEnum2[OrigensEnum2["PONTO_PRE_ASSINALADO"] = 12] = "PONTO_PRE_ASSINALADO";
  OrigensEnum2[OrigensEnum2["CONTROLE_ACESSO"] = 13] = "CONTROLE_ACESSO";
  return OrigensEnum2;
})(OrigensEnum || {});
function parserRegisterOrigin(originId) {
  const origemRegistros = [
    { [Origens[1]]: "#FF9393" },
    { [Origens[2]]: "#FE6B6B" },
    { [Origens[3]]: "#CBCB8E" },
    { [Origens[4]]: "#B3D9FF" },
    { [Origens[5]]: "#B9FFB9" },
    { [Origens[6]]: "#bdb0fa" },
    { [Origens[7]]: "#FF80C0" },
    { [Origens[8]]: "#fafa98" },
    { [Origens[9]]: "#00df3f" },
    { [Origens[10]]: "#00bedf" },
    { [Origens[11]]: "#ff7f00" },
    { [Origens[12]]: "#ff0ddd" },
    { [Origens[13]]: "#7b3306" }
  ];
  const arrayProps = [];
  for (const origin of origemRegistros) {
    for (const [key, value] of Object.entries(origin)) {
      arrayProps.push({
        title: key.split("_").join(" "),
        color: value,
        exibirComprovante: !!originId && [
          4 /* BIOMETRIA_REP_TERMINAL */,
          5 /* TECLADO_REP_TERMINAL */,
          7 /* APLICATIVO_PONTO */,
          9 /* APLICATIVO_PONTO_WEB */,
          10 /* APLICATIVO_PONTO_COLETIVO */,
          13 /* CONTROLE_ACESSO */
        ].includes(Number(originId))
      });
    }
  }
  if (!originId) {
    return arrayProps;
  }
  if (originId < 0 || originId > origemRegistros.length) {
    return {};
  }
  return arrayProps[originId - 1];
}

// src/@base/calcular-jornada.ts
function calcularJornada(params, ...horarios) {
  const {
    utilizaReducao = false,
    horarioInicioAdicionalNoturno,
    horarioFimAdicionalNoturno
  } = params === false ? {} : params;
  if (horarios.length % 2 !== 0) horarios.pop();
  if (!horarios.length) return "00:00";
  let inicioNoturno = horarioInicioAdicionalNoturno, fimNoturno = horarioFimAdicionalNoturno;
  if (!inicioNoturno || !fimNoturno) {
    inicioNoturno = "00:00";
    fimNoturno = "00:00";
  }
  const pares = chunk(horarios, 2), inicioNoturnoMinutos = hoursToMinutes(inicioNoturno), fimNoturnoMinutos = hoursToMinutes(fimNoturno), fatorReducao = !utilizaReducao ? 1 : 60 / 52.5;
  function calculateWorkTime(entrada, saida) {
    if (saida >= entrada) {
      return saida - entrada;
    } else {
      const minutesInDay = hoursToMinutes("24:00");
      return minutesInDay - entrada + saida;
    }
  }
  function calculateOverlap(workStart, workEnd, nightStart, nightEnd) {
    if (nightStart === 0 && nightEnd === 0) {
      return 0;
    }
    const minutesInDay = hoursToMinutes("24:00");
    if (workEnd < workStart) {
      workEnd += minutesInDay;
    }
    if (nightEnd < nightStart) {
      nightEnd += minutesInDay;
    }
    if (workEnd > minutesInDay && nightEnd <= minutesInDay) {
      const overlapBefore = Math.max(
        0,
        Math.min(minutesInDay, nightEnd) - Math.max(workStart, nightStart)
      );
      const overlapAfter = Math.max(
        0,
        Math.min(workEnd - minutesInDay, nightEnd) - Math.max(0, nightStart)
      );
      return overlapBefore + overlapAfter;
    }
    if (nightEnd > minutesInDay && workEnd <= minutesInDay) {
      const overlapBefore = Math.max(
        0,
        Math.min(workEnd, minutesInDay) - Math.max(workStart, nightStart)
      );
      const overlapAfter = Math.max(
        0,
        Math.min(workEnd, nightEnd - minutesInDay) - Math.max(workStart, 0)
      );
      return overlapBefore + overlapAfter;
    }
    return Math.max(
      0,
      Math.min(workEnd, nightEnd) - Math.max(workStart, nightStart)
    );
  }
  const result = pares.reduce((acc, [entrada, saida]) => {
    if (!entrada || !saida) return acc;
    const entradaMinutos = hoursToMinutes(entrada);
    const saidaMinutos = hoursToMinutes(saida);
    const totalTrabalhado = calculateWorkTime(entradaMinutos, saidaMinutos);
    const minutosNoturnosComReducao = calculateOverlap(
      entradaMinutos,
      saidaMinutos,
      inicioNoturnoMinutos,
      fimNoturnoMinutos
    );
    const minutosNormais = totalTrabalhado - minutosNoturnosComReducao;
    const totalComReducao = minutosNormais + minutosNoturnosComReducao * fatorReducao;
    return acc + totalComReducao;
  }, 0);
  return minutesToHours(result);
}

// src/@base/cnpj-valido/index.ts
function isCnpjValido(arg) {
  const cnpj = String(arg).toUpperCase().replace(/[^A-Z0-9]+/g, "");
  if (cnpj.length !== 14) return false;
  const invalidCnpjRegex = /^([A-Z0-9])\1{13}$/;
  if (invalidCnpjRegex.test(cnpj)) return false;
  const firstPartLength = cnpj.length - 2;
  const numberPart = cnpj.substring(0, firstPartLength);
  const digitPart = cnpj.substring(firstPartLength);
  let sum = 0;
  let position = firstPartLength - 7;
  for (const char of numberPart) {
    sum += (char.charCodeAt(0) - 48) * position--;
    if (position < 2) {
      position = 9;
    }
  }
  const firstDigitResult = String(sum % 11 < 2 ? 0 : 11 - sum % 11);
  if (firstDigitResult !== digitPart.charAt(0)) return false;
  const secondNumbersLength = firstPartLength + 1;
  const secondNumberPart = cnpj.substring(0, secondNumbersLength);
  sum = 0;
  position = secondNumbersLength - 7;
  for (const char of secondNumberPart) {
    sum += (char.charCodeAt(0) - 48) * position--;
    if (position < 2) {
      position = 9;
    }
  }
  const secondDigitResult = String(sum % 11 < 2 ? 0 : 11 - sum % 11);
  return secondDigitResult === digitPart.charAt(1);
}
function isCaepfValido(value) {
  const caepf = String(value).replace(/[^0-9]*/g, "");
  if (caepf.length !== 14) return false;
  const caepfBase = caepf.slice(0, 12);
  const digitosReverse = [...caepfBase].reverse().reduce((previousValue, currentValue) => {
    previousValue.push(Number(currentValue));
    return previousValue;
  }, []);
  const calcModuleDV = (array, nModuleInit = 9, nModuleMin = 2) => {
    let nModule = 9;
    let result = 0;
    for (let index = 0; index < array.length; index++) {
      const element = array[index];
      result += element * nModule;
      if (nModule > nModuleMin) {
        nModule = nModule - 1;
      } else {
        nModule = nModuleInit;
      }
    }
    result = result % 11;
    if (result === 10) {
      result = 0;
    }
    return result;
  };
  const firstDigitDV = calcModuleDV(digitosReverse);
  const lastDigitDV = calcModuleDV([firstDigitDV].concat(digitosReverse));
  let resultDV = firstDigitDV * 10 + lastDigitDV + 12;
  if (resultDV > 99) {
    resultDV = resultDV - 100;
  }
  let resultDVString = "";
  if (resultDV === 0) {
    resultDVString = "00";
  } else if (resultDV > 0 && resultDV < 10) {
    resultDVString = `0${resultDV}`;
  } else {
    resultDVString = String(resultDV);
  }
  const validCAEPF = `${caepfBase}${resultDVString}`;
  return caepf === validCAEPF;
}

// src/@base/cpf-valido/index.ts
var invalidos = [
  "00000000000",
  "11111111111",
  "22222222222",
  "33333333333",
  "44444444444",
  "55555555555",
  "66666666666",
  "77777777777",
  "88888888888",
  "99999999999",
  "12345678909",
  "01234567890"
];
var cpfValido = (arg) => {
  const cpf = String(arg).replace(/[^0-9]/g, "").trim();
  let soma = 0;
  let resto;
  let digito1;
  let digito2;
  let cpfComDigito;
  if (cpf.length !== 11) {
    return false;
  }
  if (invalidos.includes(cpf)) {
    return false;
  }
  const cpfSemDigito = cpf.substring(0, 9);
  cpfComDigito = cpf.substring(0, 11);
  for (let i = 0; i < 9; i++) {
    soma += Number.parseInt(cpfSemDigito.charAt(i), 10) * (10 - i);
  }
  resto = soma % 11;
  if (resto < 2) {
    digito1 = 0;
  } else {
    digito1 = 11 - resto;
  }
  cpfComDigito = cpfComDigito + digito1;
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += Number.parseInt(cpfComDigito.charAt(i), 10) * (11 - i);
  }
  resto = soma % 11;
  if (resto < 2) {
    digito2 = 0;
  } else {
    digito2 = 11 - resto;
  }
  const digitoVerificador = cpf.charAt(9) + cpf.charAt(10);
  return digito1.toString() + digito2.toString() === digitoVerificador;
};

// src/@base/dataURI.ts
async function encodeFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

// src/@base/deep/diff.ts
function deepDiff(fromObject, toObject) {
  const changes = {};
  const buildPath = (path, obj, key) => {
    const origVal = obj[key];
    if (path === void 0) {
      if (Array.isArray(origVal)) {
        changes[key] = [];
      } else if (origVal instanceof Date) {
        changes[key] = origVal;
      } else if (typeof origVal === "object" && origVal !== null) {
        changes[key] = {};
      }
    } else {
      if (Array.isArray(origVal)) {
        path[key] = [];
      } else if (origVal instanceof Date) {
        path[key] = origVal;
      } else if (typeof origVal === "object" && origVal !== null) {
        path[key] = {};
      }
    }
    return path === void 0 ? [changes, key] : [path, key];
  };
  const walk = (fromObj, toObj, path) => {
    for (const key of Object.keys(fromObj)) {
      const [obj, objKey] = buildPath(path, fromObj, key);
      if (!(key in toObj)) {
        obj[objKey] = { from: fromObj[key] };
      }
    }
    for (const [key, to] of Object.entries(toObj)) {
      const isLast = key in fromObj;
      const [obj, objKey] = buildPath(path, fromObj, key);
      if (isLast) {
        const from = fromObj[key];
        if (from instanceof Date && to instanceof Date) {
          if (from.getTime() !== to.getTime()) {
            obj[objKey] = { __old: from, __new: to };
          } else {
            delete obj[objKey];
          }
        } else if (from !== to) {
          if (typeof to === "object" && to !== null && typeof from === "object" && from !== null) {
            walk(from, to, obj[objKey]);
          } else {
            obj[objKey] = { __old: from, __new: to };
          }
        } else {
          delete obj[objKey];
        }
      } else {
        obj[objKey] = { to };
      }
    }
  };
  walk(fromObject, toObject);
  return changes;
}

// src/@base/estados/index.ts
import axios from "axios";
var estados = [
  {
    nome: "AC",
    id: 1
  },
  {
    nome: "AL",
    id: 2
  },
  {
    nome: "AP",
    id: 3
  },
  {
    nome: "AM",
    id: 4
  },
  {
    nome: "BA",
    id: 5
  },
  {
    nome: "CE",
    id: 6
  },
  {
    nome: "DF",
    id: 7
  },
  {
    nome: "ES",
    id: 8
  },
  {
    nome: "GO",
    id: 9
  },
  {
    nome: "MA",
    id: 10
  },
  {
    nome: "MT",
    id: 11
  },
  {
    nome: "MS",
    id: 12
  },
  {
    nome: "MG",
    id: 13
  },
  {
    nome: "PA",
    id: 14
  },
  {
    nome: "PB",
    id: 15
  },
  {
    nome: "PR",
    id: 16
  },
  {
    nome: "PE",
    id: 17
  },
  {
    nome: "PI",
    id: 18
  },
  {
    nome: "RJ",
    id: 19
  },
  {
    nome: "RN",
    id: 20
  },
  {
    nome: "RS",
    id: 21
  },
  {
    nome: "RO",
    id: 22
  },
  {
    nome: "RR",
    id: 23
  },
  {
    nome: "SC",
    id: 24
  },
  {
    nome: "SP",
    id: 25
  },
  {
    nome: "SE",
    id: 26
  },
  {
    nome: "TO",
    id: 27
  },
  {
    nome: "ES",
    id: 28
  }
];
function getCountryValue(name) {
  const country = estados.find((country2) => country2.nome === name);
  return country ? String(country.id) : "";
}
var estadosV2 = [
  { id: 11, nome: "Rond\xF4nia", sigla: "RO" },
  { id: 12, nome: "Acre", sigla: "AC" },
  { id: 13, nome: "Amazonas", sigla: "AM" },
  { id: 14, nome: "Roraima", sigla: "RR" },
  { id: 15, nome: "Par\xE1", sigla: "PA" },
  { id: 16, nome: "Amap\xE1", sigla: "AP" },
  { id: 17, nome: "Tocantins", sigla: "TO" },
  { id: 21, nome: "Maranh\xE3o", sigla: "MA" },
  { id: 22, nome: "Piau\xED", sigla: "PI" },
  { id: 23, nome: "Cear\xE1", sigla: "CE" },
  { id: 24, nome: "Rio Grande do Norte", sigla: "RN" },
  { id: 25, nome: "Para\xEDba", sigla: "PB" },
  { id: 26, nome: "Pernambuco", sigla: "PE" },
  { id: 27, nome: "Alagoas", sigla: "AL" },
  { id: 28, nome: "Sergipe", sigla: "SE" },
  { id: 29, nome: "Bahia", sigla: "BA" },
  { id: 31, nome: "Minas Gerais", sigla: "MG" },
  { id: 32, nome: "Esp\xEDrito Santo", sigla: "ES" },
  { id: 33, nome: "Rio de Janeiro", sigla: "RJ" },
  { id: 35, nome: "S\xE3o Paulo", sigla: "SP" },
  { id: 41, nome: "Paran\xE1", sigla: "PR" },
  { id: 42, nome: "Santa Catarina", sigla: "SC" },
  { id: 43, nome: "Rio Grande do Sul", sigla: "RS" },
  { id: 50, nome: "Mato Grosso do Sul", sigla: "MS" },
  { id: 51, nome: "Mato Grosso", sigla: "MT" },
  { id: 52, nome: "Goi\xE1s", sigla: "GO" },
  { id: 53, nome: "Distrito Federal", sigla: "DF" }
].sort((a, b) => a.nome.localeCompare(b.nome));
async function getCidadesByUf(UF) {
  const url = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${UF}/municipios?view=nivelado`;
  const res = await axios.get(url).then((res2) => res2.data);
  const cidades = res.map((item) => ({
    id: item["municipio-id"],
    nome: item["municipio-nome"],
    uf: item["UF-sigla"]
  }));
  return cidades;
}
async function getCidadeByIBGE(ibge) {
  const url = `https://servicodados.ibge.gov.br/api/v1/localidades/municipios/${ibge}?view=nivelado`;
  const res = await axios.get(url).then((res2) => res2.data);
  return {
    nome: res["municipio-nome"],
    uf: res["UF-sigla"]
  };
}

// src/@base/existeFolga.ts
import dayjs3 from "dayjs";
import utc2 from "dayjs/plugin/utc";
function exiteFolga(dataVerificar, escala, escalaFuncionario, ultimaFolga) {
  dayjs3.extend(utc2);
  const trabalhaMin = escala.tipo === "DIARISTA" ? parseInt(escala.trabalha) * 60 * 24 : hoursToMinutes(escala.trabalha);
  const folgaMin = escala.tipo === "DIARISTA" ? parseInt(escala.folga) * 60 * 24 : hoursToMinutes(escala.folga);
  const dtVerificar = dayjs3.utc(dataVerificar).toDate();
  const dtInicial = dayjs3.utc(escalaFuncionario.dtInicial).toDate();
  const dtFinal = escalaFuncionario.dtFinal ? dayjs3.utc(escalaFuncionario.dtFinal).toDate() : null;
  if (dtVerificar < dtInicial) return false;
  if (dtFinal && dtVerificar > dtFinal) return false;
  const minutesPerDay = 60 * 24;
  const trabalhaDias = trabalhaMin / minutesPerDay;
  const ciclo = (trabalhaMin + folgaMin) / minutesPerDay;
  if (!ultimaFolga) {
    const diff2 = dayjs3(dtVerificar).diff(dtInicial, "day");
    const posicaoNoCiclo2 = diff2 % ciclo;
    return posicaoNoCiclo2 >= trabalhaDias;
  }
  const dtUltimaFolga = dayjs3.utc(ultimaFolga.data).toDate();
  const diff = dayjs3(dtVerificar).diff(dtUltimaFolga, "day");
  const posicaoNoCiclo = (diff + ciclo - 1) % ciclo;
  return posicaoNoCiclo >= trabalhaDias;
}

// src/@base/filter.ts
function onlyDefined(value) {
  return value !== void 0 && value !== null;
}
function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}
function uniqueBy(arr, keySelector) {
  const keyFn = typeof keySelector === "function" ? keySelector : (item) => item[keySelector];
  const seen = /* @__PURE__ */ new Map();
  arr.forEach((item) => {
    const key = keyFn(item);
    if (!seen.has(key)) {
      seen.set(key, item);
    }
  });
  return Array.from(seen.values());
}

// src/@base/filter-changed-data/index.ts
function filterChangedData(currentData, newData) {
  let changedData = {};
  if (typeof currentData === "object" && !Array.isArray(currentData)) {
    for (const key of Object.keys(newData)) {
      const currentValue = currentData[key];
      const newValue = newData[key];
      if (typeof currentValue === "object" && currentValue !== null && newValue !== null && Object.keys(currentValue).length !== 0) {
        const changedObject = filterChangedData(
          currentValue,
          newValue
        );
        if (Object.keys(changedObject).length > 0) {
          changedData[key] = changedObject;
        }
      } else if (currentValue !== newValue && newValue !== void 0) {
        changedData[key] = newValue;
      }
    }
  } else if (Array.isArray(currentData)) {
    if (currentData.length !== newData.length) {
      changedData = newData;
    }
  }
  return changedData;
}

// src/@base/horas-decimais.ts
function horasDecimaisDisplay(arg) {
  const value = arg ? arg : "00:00";
  const [h, m] = value.split(":");
  const horas = Number.parseInt(h);
  const minutos = Number.parseInt(m) / 60 * 100;
  const display = `${horas},${Math.round(minutos)}`;
  return display === "0,0" ? `${value}` : `(${display}) ${value}`;
}

// src/@base/insensitive.ts
function insensitive(value, to = "upper") {
  const normalized = normalize(value?.trim() || "");
  return to === "upper" ? normalized.toUpperCase() : normalized.toLowerCase();
}
function normalize(value) {
  return String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// src/@base/instance.ts
function Instance(arg) {
  return arg;
}

// src/@base/jsonFormat.ts
function JSONFormat(data) {
  if (typeof data === "string") return JSON.parse(data);
  return data;
}

// src/@base/locais-centros-filtering.ts
import dayjs4 from "dayjs";
function filterLocaisCentro(locais = [], centroCustoIds = [], accessor = (item) => item.Funcionario) {
  return (arrItem) => {
    const item = accessor(arrItem);
    if (!item) return false;
    const centrosCustoValidos = centroCustoIds.filter((id) => id > 0);
    const hasCentroCusto = centrosCustoValidos.length > 0 ? getCentroCustoAtual(item).some(
      (cc) => centrosCustoValidos.includes(Number(cc.centroCustoId))
    ) : true;
    const hasLocal = locais.length && Array.isArray(item.FuncionarioLocais) ? item.FuncionarioLocais.some(
      (local) => locais.includes(Number(local.localId))
    ) : true;
    return hasCentroCusto && hasLocal;
  };
}
function getCentroCustoAtual({
  FuncionarioCentroCusto
}, data = dayjs4()) {
  if (!FuncionarioCentroCusto || !FuncionarioCentroCusto.length) return [];
  return FuncionarioCentroCusto.filter(
    (cc) => !cc.dtFinal || dayjs4(cc.dtFinal).isAfter(data, "day")
  ).sort(
    (a, b) => "CentroCusto" in a && "CentroCusto" in b ? a.CentroCusto.codigo.localeCompare(b.CentroCusto.codigo) : dayjs4(a.dtInicial).diff(dayjs4(b.dtInicial))
  );
}
function groupByCentroCusto(items, data = dayjs4()) {
  const map = /* @__PURE__ */ new Map();
  items.forEach((item) => {
    const centrosCusto = getCentroCustoAtual(item, data);
    if (centrosCusto.length === 0) {
      const key = -1;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(item);
    } else {
      centrosCusto.forEach((cc) => {
        const key = cc.centroCustoId;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(item);
      });
    }
  });
  return map;
}

// src/@base/loopConcat.ts
function loopConcat(data, times = 50) {
  if (!Array.isArray(data) || times <= 0) return data;
  const dataLength = data.length;
  const result = new Array(dataLength * times);
  for (let i = 0; i < times; i++) {
    const offset = i * dataLength;
    for (let j = 0; j < dataLength; j++) {
      result[offset + j] = data[j];
    }
  }
  return result;
}

// src/@base/masks/index.ts
var REMOVE_MASK_CPF = /([.\-])/g;
function cpfMask(value) {
  const mask = value.toString().slice(0, 11).padStart(11, "0").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (mask === "000.000.000-00") {
    return "";
  }
  return mask;
}
var REMOVE_MASK_CNPJ = /([.\/\-])/g;
function cnpjMask(value) {
  const mask = value.slice(0, 14).padStart(14, "0").toUpperCase().replace(
    /([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
    "$1.$2.$3/$4-$5"
  );
  if (mask === "00.000.000/0000-00") {
    return "";
  }
  return mask;
}
var REMOVE_MASK_CEP = REMOVE_MASK_CPF;
function cepMask(value) {
  const mask = value.toString().slice(0, 8).padStart(8, "0").replace(/(\d{5})(\d{3})/, "$1-$2");
  if (mask === "00000-000") {
    return "";
  }
  return mask;
}
var REMOVE_MASK_CAEPF = REMOVE_MASK_CNPJ;
function caepfMask(value) {
  const mask = value.toString().slice(0, 14).padStart(14, "0").replace(/(\d{3})(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3/$4-$5");
  if (mask === "000.000.000/000-00") {
    return "";
  }
  return mask;
}

// src/@base/merge-porcentagem/porcentagem.ts
var MappedPorcentagem = class _MappedPorcentagem {
  _props;
  _map;
  constructor(value) {
    if (!value) {
      this._props = {};
      this._map = new Map(this.entries);
    } else if (typeof value === "string") {
      this._props = JSON.parse(value);
      this._map = new Map(this.entries);
    } else {
      this._props = value;
      this._map = new Map(this.entries);
    }
  }
  get value() {
    return Object.entries(this._props).reduce((acc, [key, value]) => {
      if (value !== "00:00") {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
  get entries() {
    return Object.entries(this._props).filter(([k]) => k !== "version").map(([k, v]) => [k, v]);
  }
  get size() {
    return this._map.size;
  }
  get total() {
    const totalMinutes = this.entries.reduce((acc, [_, value]) => {
      return acc + hoursToMinutes(value);
    }, 0);
    return minutesToHours(totalMinutes);
  }
  get(key) {
    return this._map.get(String(key));
  }
  has(key) {
    return this._map.has(String(key));
  }
  set(arg1, arg2) {
    const key = String(arg1);
    const value = typeof arg2 === "number" ? minutesToHours(arg2) : arg2;
    if (arg2 !== "00:00") {
      this._props[key] = value;
      this._map.set(key, value);
    } else {
      delete this._props[key];
      this._map.delete(key);
    }
    return this;
  }
  keys() {
    const keys = [];
    for (const key of this._map.keys()) {
      keys.push(key);
    }
    return keys;
  }
  delete(key) {
    const k = String(key);
    const isDeleted = this._map.delete(k);
    if (isDeleted) {
      delete this._props[k];
    }
    return isDeleted;
  }
  equals(porcentagem) {
    if (typeof porcentagem === "string") {
      return JSON.stringify(this._props) === porcentagem;
    }
    if (porcentagem instanceof _MappedPorcentagem) {
      return JSON.stringify(this._props) === JSON.stringify(porcentagem.value);
    }
    return JSON.stringify(this._props) === JSON.stringify(porcentagem);
  }
  /**
   * Soma as porcentagens atuais com uma nova porcentagem.
   * @param porcentagem A nova porcentagem a ser somada.
   * @returns Uma nova instância de MappedPorcentagem com a porcentagem somada.
   * @example <caption>Se a porcentagem em ambas não existir, ela é adicionada.</caption>
   * const a = new MappedPorcentagem({ 50: '03:40', 100: '00:30' });
   * const b = new MappedPorcentagem({ 50: '01:00', 75: '00:30' });
   * const result = a.sum(b); // { 50: '04:40', 75: '00:30' 100: '00:30' }
   */
  sum(porcentagem) {
    if (!porcentagem) return this;
    if (typeof porcentagem === "string") {
      return new _MappedPorcentagem(
        mergePorcentagem(this._props, JSON.parse(porcentagem))
      );
    }
    if (porcentagem instanceof _MappedPorcentagem) {
      return new _MappedPorcentagem(
        mergePorcentagem(this._props, porcentagem.value)
      );
    }
    return new _MappedPorcentagem(mergePorcentagem(this._props, porcentagem));
  }
  /**
   * Faz a subtração entre a porcentagem atual e a porcentagem passada como parâmetro.
   * @param porcentagem - A porcentagem a ser subtraída da porcentagem atual.
   * Pode ser um objeto do tipo MappedPorcentagem, um objeto do tipo TypedPorcentagem ou uma string JSON.
   * @returns Um novo objeto MappedPorcentagem contendo a diferença entre as porcentagens.
   * @examples
   * @example <caption>Se a porcentagem de tempo for zerada, ela é removida do objeto.</caption>
   * const a = new MappedPorcentagem({ 50: '03:40', 100: '00:30' });
   * const b = new MappedPorcentagem({ 50: '01:00', 100: '00:30' });
   * const result = a.subtract(b); // { 50: '02:40' }
   * @example <caption>Se a porcentagem a ser comparada for maior que a porcentagem atual, a porcentagem atual é removida do objeto e se uma porcentagem da porcentagem a ser comparada não existir na porcentagem atual, ela é ignorada.</caption>
   * const a = new MappedPorcentagem({ 50: '03:40', 100: '00:30' });
   * const b = new MappedPorcentagem({ 50: '04:00', 75: '00:30' });
   * const result = a.subtract(b); // { 100: '00:30' }
   */
  subtract(porcentagem) {
    if (!porcentagem) return this;
    if (typeof porcentagem === "string") {
      return new _MappedPorcentagem(
        diffPorcentagem(this._props, JSON.parse(porcentagem))
      );
    }
    if (porcentagem instanceof _MappedPorcentagem) {
      return new _MappedPorcentagem(
        diffPorcentagem(this._props, porcentagem.value)
      );
    }
    return new _MappedPorcentagem(diffPorcentagem(this._props, porcentagem));
  }
  /**
   * Subtrai igual MappedPorcentagem.subtract, retorna o resultado da subtração e o restante da porcentagem.
   * @example
   * const a = new MappedPorcentagem({ 50: '12:46' })
   * const b = new MappedPorcentagem({ 50: '16:04' })
   * const resultado = a.remainder(b)
   * // resultado.result = {}
   * // resultado.remainder = { 50: '03:18' }
   * @param porcentagem - A porcentagem a ser subtraída da porcentagem atual.
   * @returns Um objeto contendo o resultado da subtração e o restante da porcentagem.
   */
  remainder(porcentagem) {
    if (!porcentagem) porcentagem = new _MappedPorcentagem({}).value;
    if (typeof porcentagem === "string")
      porcentagem = JSON.parse(porcentagem);
    if (porcentagem instanceof _MappedPorcentagem)
      porcentagem = porcentagem.value;
    const result = diffPorcentagem(
      this._props,
      porcentagem
    );
    const remainder = diffPorcentagem(
      porcentagem,
      this._props
    );
    return {
      result: new _MappedPorcentagem(result),
      remainder: new _MappedPorcentagem(remainder)
    };
  }
};
function compensarPorcentagens(porcentagem, compensacoes) {
  return Object.entries(porcentagem).reduce((acc, [key, value]) => {
    const fator = Number(compensacoes.get(key) ?? 1);
    if ([-1, 0].includes(fator)) return acc.set(key, value);
    const multiplicador = fator / 100 + 1;
    return acc.set(key, hoursToMinutes(value) * multiplicador);
  }, new MappedPorcentagem({}));
}
function formatPorcentagemRegra(porcentagem) {
  return isNaN(Number(porcentagem)) ? porcentagem : `${porcentagem}%`;
}

// src/@base/merge-porcentagem/index.ts
var HH_MM_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
function mergePorcentagem(a, b) {
  const result = Object.assign({}, a);
  const entries = Object.entries(b).filter(([key]) => key !== "version");
  for (const [key, value] of entries) {
    const minutes = hoursToMinutes(value);
    if (Object.hasOwn(result, key)) {
      const currentMinutes = hoursToMinutes(result[key]);
      result[key] = minutesToHours(
        currentMinutes + minutes
      );
    } else {
      result[key] = value;
    }
  }
  return result;
}
function diffPorcentagem(a, b) {
  const result = {};
  const copyA = Object.assign({}, a);
  const copyB = Object.assign({}, b);
  const entries = Object.entries(b).filter(([key]) => key !== "version");
  for (const [key, value] of entries) {
    const minutes = hoursToMinutes(value);
    if (Object.hasOwn(copyA, key)) {
      const currentMinutes = hoursToMinutes(copyA[key]);
      if (currentMinutes > minutes) {
        result[key] = minutesToHours(
          currentMinutes - minutes
        );
      } else {
        copyA[key] = "00:00";
      }
    }
  }
  for (const [key, value] of Object.entries(copyA)) {
    if (!Object.hasOwn(copyB, key)) {
      result[key] = value;
    }
  }
  return result;
}

// src/@base/mount-url/index.ts
var mountUrl = (url, record) => {
  const arr = [];
  const queryParams = [];
  for (const [key, val] of Object.entries(record)) {
    arr.push(`${key}=${val}`);
  }
  for (let index = 0; index < arr.length; index++) {
    if (index === 0) {
      queryParams.push(`?${arr[index]}`);
    } else {
      queryParams.push(arr[index]);
    }
  }
  return `${url}${queryParams.join("&")}`;
};

// src/@base/nullish.ts
var nullish = [null, void 0];
function replaceNullish(input, replaceWith = "") {
  if (nullish.includes(input)) return replaceWith;
  if (Array.isArray(input))
    return input.map((item) => replaceNullish(item, replaceWith));
  if (typeof input === "object") {
    for (const key in input) {
      if (nullish.includes(input[key]))
        input[key] = replaceWith;
      else input[key] = replaceNullish(input[key], replaceWith);
    }
  }
  return input;
}
function deleteNullish(input) {
  if (Array.isArray(input))
    return input.map((item) => deleteNullish(item)).filter(onlyDefined);
  if (typeof input === "object") {
    const obj = input;
    for (const key in obj) {
      if (nullish.includes(obj[key])) delete obj[key];
      else obj[key] = deleteNullish(obj[key]);
    }
  }
  return input;
}

// src/@base/object.ts
function pick(object, props) {
  if (!object || typeof object !== "object") return {};
  return Object.fromEntries(props.map((key) => [key, object[key]]));
}
function omit(object, props) {
  if (!Array.isArray(props)) props = [props];
  if (object === void 0 || !object || typeof object !== "object")
    return {};
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !props.includes(key))
  );
}
function groupBy(arr, criteria) {
  if (!arr || !criteria) return {};
  const grouped = /* @__PURE__ */ Object.create(null);
  if (typeof criteria === "function") {
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      const key = criteria(item);
      const existing = grouped[key];
      if (existing) {
        existing.push(item);
      } else {
        grouped[key] = [item];
      }
    }
    return grouped;
  }
  const prop = criteria;
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    const key = item[prop];
    const existing = grouped[key];
    if (existing) {
      existing.push(arr[i]);
    } else {
      grouped[key] = [arr[i]];
    }
  }
  return grouped;
}

// src/@base/path.ts
var safePathName = (name, espacos = "-", f = "lower") => {
  let path = name.replace(/\s/g, espacos);
  if (f === "lower") path = path.toLowerCase();
  else path = path.toUpperCase();
  return path.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[\\\/\:\*\?\"\<\>\|]/g, "");
};

// src/@base/periodo-ponto.ts
import dayjs5 from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc3 from "dayjs/plugin/utc";
var TrocaInicioPontoMapped = class {
  competencia;
  dataInicial;
  dataFinal;
  diaInicioPonto;
  diaAnterior;
  constructor(data) {
    dayjs5.extend(utc3);
    dayjs5.extend(isBetween);
    this.competencia = dayjs5.utc(data.competencia, "YYYY-MM");
    this.diaInicioPonto = data.diaInicioPonto;
    this.diaAnterior = data.diaAnterior;
    this.dataInicial = dayjs5.utc(data.dataInicial);
    this.dataFinal = dayjs5.utc(data.dataFinal);
  }
  isAfter(competencia) {
    return this.competencia.isAfter(
      dayjs5.utc(competencia, "YYYY-MM"),
      "month"
    );
  }
  contains(data) {
    return data.isBetween(this.dataInicial, this.dataFinal, "day", "[]");
  }
};
var PeriodoPonto = class {
  constructor(inicio, fim, isHorarioNoturno) {
    this.isHorarioNoturno = isHorarioNoturno;
    dayjs5.extend(utc3);
    dayjs5.extend(isSameOrAfter);
    let dataInicial;
    let dataFinal;
    if (typeof inicio === "string") {
      dataInicial = dayjs5.utc(inicio, "YYYY-MM-DD", true);
    } else {
      dataInicial = inicio;
    }
    if (typeof fim === "string") {
      dataFinal = dayjs5.utc(fim, "YYYY-MM-DD", true);
    } else {
      dataFinal = fim;
    }
    if (!dataInicial.isValid()) {
      throw new Error(
        "Data de in\xEDcio inv\xE1lida: ".concat(inicio?.toString())
      );
    }
    if (!dataFinal.isValid()) {
      throw new Error("Data de fim inv\xE1lida: ".concat(fim?.toString()));
    }
    this.periodoPonto = [dataInicial, dataFinal];
  }
  periodoPonto;
  /**
   * Obtém um array com a data inicial e final do período de ponto.
   * @param formatted Quando verdadeiro, retorna string, quando falso, retorna Dayjs
   * @returns [dataInicial, dataFinal]
   */
  getPeriodo(formatted = false) {
    return formatted ? [
      this.periodoPonto[0].format("YYYY-MM-DD"),
      this.periodoPonto[1].format("YYYY-MM-DD")
    ] : this.periodoPonto;
  }
  /** Obtém um array com todos os dias do período de ponto no formato 'YYYY-MM-DD' */
  get dias() {
    const inicio = this.periodoPonto[0];
    const final = this.periodoPonto[1];
    return Array.from({ length: final.diff(inicio, "day") + 1 }).map((_, i) => inicio.add(i, "day").format("YYYY-MM-DD")).filter(onlyUnique);
  }
  /** Obtém uma string que representa o período de ponto no formato 'Período de DD/MM/YYYY a DD/MM/YYYY' */
  get label() {
    let inicio = this.periodoPonto.at(0), fim = this.periodoPonto.at(-1);
    if (!inicio || !fim) return "";
    if (this.isHorarioNoturno) {
      inicio = inicio.add(1, "day");
      fim = fim.subtract(1, "day");
    }
    return `Per\xEDodo de ${inicio.format("DD/MM/YYYY")} a ${fim.format(
      "DD/MM/YYYY"
    )}`;
  }
  /**
   * Obtém um Array com as competências (YYYY-MM) envolvidas no período de ponto.
   */
  getCompetencias({
    diaInicioPonto,
    trocasInicioPonto
  }) {
    const competencias = /* @__PURE__ */ new Set();
    const [dataInicial, dataFinal] = this.getPeriodo();
    let current = dataInicial;
    const end = dataFinal;
    const periodosCompetencia = new PeriodosCompetencia(
      diaInicioPonto,
      trocasInicioPonto,
      this.isHorarioNoturno
    );
    while (current.isSameOrAfter(end, "day") === false || current.isSame(end, "day")) {
      const troca = Array.from(
        periodosCompetencia.trocasInicioPonto.values()
      ).find((t) => t.contains(current));
      if (troca) {
        competencias.add(troca.competencia.format("YYYY-MM"));
      } else {
        let competencia;
        if (diaInicioPonto === 1) {
          competencia = current.startOf("month");
        } else if (current.date() >= diaInicioPonto) {
          competencia = current.add(1, "month").startOf("month");
        } else {
          competencia = current.startOf("month");
        }
        competencias.add(competencia.format("YYYY-MM"));
      }
      current = current.add(1, "day");
    }
    return Array.from(competencias);
  }
};
var PeriodosCompetencia = class {
  constructor(diaInicioPonto, trocasInicioPonto, isHorarioNoturno) {
    this.isHorarioNoturno = isHorarioNoturno;
    dayjs5.extend(utc3);
    this.diaInicioPonto = diaInicioPonto;
    this.trocasInicioPonto = new Map(
      trocasInicioPonto.sort(
        (a, b) => dayjs5.utc(a.competencia, "YYYY-MM").valueOf() - dayjs5.utc(b.competencia, "YYYY-MM").valueOf()
      ).map((t) => [t.competencia, new TrocaInicioPontoMapped(t)])
    );
  }
  /** Dia início do ponto da empresa */
  diaInicioPonto;
  trocasInicioPonto;
  /**
   * Obtém o período de ponto para uma dada competência, considerando possíveis trocas de início de ponto.
   * @param competencia Competência no formato YYYY-MM
   * @returns Objeto PeriodoPonto com dataInicial e dataFinal do período de ponto
   * @throws Erro se a competência estiver em formato inválido
   */
  getPeriodoPonto(competencia) {
    const competenciaValida = /^(\d{4})-(0[1-9]|1[0-2])$/.test(competencia);
    if (!competenciaValida) {
      throw new Error("Compet\xEAncia inv\xE1lida. Formato esperado: YYYY-MM");
    }
    const troca = this.trocasInicioPonto.get(competencia);
    let diaInicioPonto;
    if (troca) {
      diaInicioPonto = troca.diaInicioPonto;
    } else {
      const trocaPosterior = Array.from(
        this.trocasInicioPonto.values()
      ).find((t) => t.isAfter(competencia));
      if (trocaPosterior) diaInicioPonto = trocaPosterior.diaAnterior;
    }
    diaInicioPonto ??= this.diaInicioPonto;
    if (troca) {
      let inicio2 = troca.dataInicial;
      let fim2 = troca.dataFinal;
      if (this.isHorarioNoturno) {
        inicio2 = inicio2.subtract(1, "day");
        fim2 = fim2.add(1, "day");
      }
      return new PeriodoPonto(inicio2, fim2, this.isHorarioNoturno);
    }
    let inicio = dayjs5.utc(competencia, "YYYY-MM").set("date", diaInicioPonto);
    if (diaInicioPonto > 1) inicio = inicio.subtract(1, "month");
    let fim = inicio.clone().add(1, "month").subtract(1, "day");
    if (this.isHorarioNoturno) {
      inicio = inicio.subtract(1, "day");
      fim = fim.add(1, "day");
    }
    return new PeriodoPonto(inicio, fim, this.isHorarioNoturno);
  }
};
var TrocasEntity = class {
  data;
  diaInicioPontoDefault;
  constructor(data, diaInicioPontoDefault) {
    this.data = [...data].sort(
      (a, b) => dayjs5.utc(a.competencia, "YYYY-MM").valueOf() - dayjs5.utc(b.competencia, "YYYY-MM").valueOf()
    );
    this.diaInicioPontoDefault = diaInicioPontoDefault;
  }
  getDiaInicioPonto(competencia) {
    const comTroca = this.data.find((t) => t.competencia === competencia);
    if (comTroca) return comTroca.diaInicioPonto;
    const proximaCompetencia = this.data.find(
      (it) => dayjs5(it.competencia, "YYYY-MM").isAfter(
        dayjs5(competencia, "YYYY-MM")
      )
    );
    if (proximaCompetencia) {
      return proximaCompetencia.diaAnterior;
    }
    return this.diaInicioPontoDefault;
  }
};
var PeriodoPontoFactory = class {
  static fromCompetencia(competencia, config, isHorarioNoturno = false) {
    return new PeriodosCompetencia(
      config.diaInicioPonto,
      config.trocasInicioPonto,
      isHorarioNoturno
    ).getPeriodoPonto(competencia);
  }
  static fromPeriodo(dataInicial, dataFinal, isHorarioNoturno = false) {
    return new PeriodoPonto(dataInicial, dataFinal, isHorarioNoturno);
  }
  static createTrocasEntity(trocas, diaInicioPontoDefault) {
    return new TrocasEntity(trocas, diaInicioPontoDefault);
  }
};

// src/@base/pis-valido/index.ts
var pisPasepValido = (arg) => {
  const pis = arg.replace(/[^\d]+/g, "");
  if (pis.length !== 11 || Number.isNaN(Number(pis)) || pis.match(/^(.)\1+$/))
    return false;
  const multiplicadorBase = "3298765432";
  let total = 0;
  let resto = 0;
  let multiplicando = 0;
  let multiplicador = 0;
  let digito = 99;
  for (let i = 0; i < 10; i++) {
    multiplicando = Number.parseInt(pis.substring(i, i + 1));
    multiplicador = Number.parseInt(multiplicadorBase.substring(i, i + 1));
    total += multiplicando * multiplicador;
  }
  resto = 11 - total % 11;
  resto = resto === 10 || resto === 11 ? 0 : resto;
  digito = Number.parseInt(`${pis.charAt(10)}`);
  return resto === digito;
};
var nitValido = (arg) => {
  const nit = arg.replace(/\D/g, "");
  if (nit.length !== 11) return false;
  const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += Number.parseInt(nit.charAt(i)) * pesos[i];
  }
  const resto = soma % 11;
  const digitoVerificador = resto < 2 ? 0 : 11 - resto;
  return Number.parseInt(nit.charAt(10)) === digitoVerificador;
};
function criarPisPortaria671({ cpf, tipo }) {
  if (tipo === 9) return `9${cpf}`;
  const digitos = cpf.replace(/[^\d]+/g, "").split("").map(Number);
  digitos.pop();
  const soma = digitos.reduce((acc, cur) => {
    return acc + cur;
  }, 0);
  const resto = soma % 11;
  const resultado = 11 - resto;
  const dv = resultado === 10 || resultado === 11 ? 0 : resultado;
  return `8${cpf}${dv}`;
}

// src/@base/room/index.ts
var getSocketRoom = ({ clienteId }) => {
  return btoa(`room-${clienteId}`);
};

// src/@base/update-journey/index.ts
import dayjs6 from "dayjs";
var getTypePayload = (payload) => {
  let type;
  function isAbono(payload2) {
    return payload2.quantidadeHoras !== void 0;
  }
  function isAfastamento(payload2) {
    return payload2.dataInicial !== void 0;
  }
  function isRegistro(payload2) {
    return payload2.hora !== void 0;
  }
  function isCompensacao(payload2) {
    return payload2.dataCompensacao !== void 0;
  }
  if (isAbono(payload)) {
    type = "Abono";
    return type;
  }
  if (isAfastamento(payload)) {
    type = "Afastamento";
    return type;
  }
  if (isRegistro(payload)) {
    type = "Registro";
    return type;
  }
  if (isCompensacao(payload)) {
    type = "Compensacao";
    return type;
  }
};
var handleUpdateAllRegistries = ({
  date,
  payload
}) => {
  return (previousData) => {
    const type = getTypePayload(payload);
    if (previousData) {
      const allRegistries = [...previousData];
      const index = allRegistries.findIndex((index2) => index2.dia === date);
      switch (type) {
        case "Registro": {
          const registry = payload;
          const registryAlreadyExists = allRegistries[index].registros.find((f) => {
            return f.id === registry.id;
          });
          if (registryAlreadyExists) {
            const registros = allRegistries[index].registros.filter(
              (f) => f.id !== registry.id
            );
            allRegistries[index].registros = registros;
            allRegistries[index].registros.sort((a, b) => {
              if (a.hora < b.hora) return -1;
              if (a.hora > b.hora) return 1;
              return 0;
            });
            return allRegistries;
          }
          allRegistries[index].registros.push(registry);
          allRegistries[index].registros.sort((a, b) => {
            if (a.hora < b.hora) return -1;
            if (a.hora > b.hora) return 1;
            return 0;
          });
          return allRegistries;
        }
        case "Abono": {
          const abono = payload;
          const abonoAlreadyExists = allRegistries[index].abono;
          if (abonoAlreadyExists) {
            allRegistries[index].abono = void 0;
            return allRegistries;
          }
          allRegistries[index].abono = abono;
          return allRegistries;
        }
        case "Afastamento": {
          const afastamento = payload;
          const afastamentoRange = [];
          const diaInicio = dayjs6(afastamento.dataInicial);
          const diaFinal = dayjs6(afastamento.dataFinal);
          const diff = diaFinal.diff(diaInicio, "days");
          for (let i = 0; i <= diff; i += 1) {
            const date2 = diaInicio.add(i, "days").format("YYYY-MM-DD");
            afastamentoRange.push({ date: date2, afastamento });
          }
          for (const { date: date2, afastamento: afastamento2 } of afastamentoRange) {
            const index2 = allRegistries.findIndex(
              (item) => item.dia === date2
            );
            allRegistries[index2].afastamento = afastamento2;
          }
          return allRegistries;
        }
        case "Compensacao": {
          const compensacao = payload;
          const compensacaoAlreadyExists = allRegistries[index].compensacao;
          if (compensacaoAlreadyExists) {
            allRegistries[index].compensacao = void 0;
            return allRegistries;
          }
          allRegistries[index].compensacao = compensacao;
          return allRegistries;
        }
      }
    }
    return [];
  };
};

// src/@base/upload-file/index.ts
import axios2 from "axios";
var uploadFile = async (fileUrl, baseUrl) => {
  if (!fileUrl) {
    return "";
  }
  const formData = new FormData();
  formData.append("file", fileUrl);
  const response = await axios2.post(`${baseUrl}/v1/uploads`, formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  }).catch((e) => {
    if (e.response && e.response.data) {
      return Promise.reject(e.response.data);
    }
    return Promise.reject(e);
  });
  if (!response) {
    throw new Error("Erro ao enviar arquivo");
  }
  const {
    data: { filename }
  } = response?.data;
  return filename;
};

// src/@base/void.ts
function Void(..._) {
  return;
}
export {
  DateTimePatternString,
  HH_MM_REGEX,
  Instance,
  JSONFormat,
  MappedPorcentagem,
  Origens,
  OrigensEnum,
  PeriodoPontoFactory,
  REMOVE_MASK_CAEPF,
  REMOVE_MASK_CEP,
  REMOVE_MASK_CNPJ,
  REMOVE_MASK_CPF,
  Void,
  caepfMask,
  calcularJornada,
  cepMask,
  chunk,
  cnpjMask,
  compensarPorcentagens,
  concat,
  cpfMask,
  cpfValido,
  criarPisPortaria671,
  dateTimePatternDate,
  deepDiff,
  deleteNullish,
  diffPorcentagem,
  encodeFile,
  estados,
  estadosV2,
  exiteFolga,
  filterChangedData,
  filterLocaisCentro,
  formatPorcentagemRegra,
  formatarHoras,
  getCentroCustoAtual,
  getCidadeByIBGE,
  getCidadesByUf,
  getCountryValue,
  getSocketRoom,
  groupBy,
  groupByCentroCusto,
  handleUpdateAllRegistries,
  horasDecimaisDisplay,
  hoursToMinutes,
  insensitive,
  isCaepfValido,
  isCnpjValido,
  loopConcat,
  mergePorcentagem,
  minutesToHours,
  mountUrl,
  nitValido,
  normalize,
  omit,
  onlyDefined,
  onlyUnique,
  overflowChunk,
  parseDataTransform,
  parserRegisterOrigin,
  pick,
  pisPasepValido,
  replaceNullish,
  safePathName,
  uniqueBy,
  uploadFile
};
//# sourceMappingURL=index.js.map